"use client";

import React from "react";
import { MapPin, Phone, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="35901" className="h-full flex flex-col items-center justify-center p-8 bg-primary/10 border border-primary/20 rounded-lg text-center animate-in fade-in zoom-in duration-500">
        <h3 className="text-2xl font-serif text-foreground mb-3 uppercase tracking-tight">Request Received</h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button variant="outline" className="mt-6 rounded-md border-primary/40 hover:bg-primary/20 text-foreground" onClick={() => window.location.reload()}>
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Your Name" className="border-2 border-slate-400 focus:border-primary focus:ring-0 bg-background/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="713-304-4798" className="border-2 border-slate-400 focus:border-primary focus:ring-0 bg-background/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="your@email.com" className="border-2 border-slate-400 focus:border-primary focus:ring-0 bg-background/50" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Service Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your garage door repair or installation needs..." className="min-h-[120px] border-2 border-slate-400 focus:border-primary focus:ring-0 bg-background/50" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <div className="pt-6">
        <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-white hover:bg-primary/90 font-medium uppercase tracking-wider px-4 py-2 rounded-md transition-all">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Processing...
            </> : "Get Free Estimate"}
        </Button>
      </div>
    </Form>;
}
export default function ContactUs() {
  return <section id="get-a-quote-form" className="py-24 overflow-hidden relative dark lg:py-32 bg-ring">
      {}
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />

      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-start">
          
          {}
          <div className="lg:col-span-5 space-y-16 lg:sticky lg:top-32">
            <div className="space-y-6">
              <h2 className="text-5xl tracking-tighter leading-[0.9] uppercase !font-sans !font-extrabold md:text-6xl lg:text-7xl text-background">GET A FREE QUOTE</h2>
              <p className="text-lg leading-relaxed max-w-md !text-[hsl(0,0%,100%)] !font-medium">Contact Schaefer Garage Doors for professional garage door repairs, installations, and 24/7 emergency services across Kingwood, TX and surrounding areas.</p>
            </div>

            <div className="space-y-12">
              <div className="group flex flex-col gap-4">
                <div className="flex items-center gap-3 !text-[hsl(0,0%,100%)]">
                  <MapPin className="size-5" />
                  <span className="text-xs font-bold uppercase tracking-[0.2em] !text-[hsl(0,0%,100%)]">Headquarters</span>
                </div>
                <h3 className="text-3xl text-foreground uppercase tracking-tight !font-sans md:text-4xl">
                  808 Russell Palmer Rd #255<br />Kingwood, TX 77339
                </h3>
              </div>

              <div className="group flex flex-col gap-4">
                <div className="flex items-center gap-3 text-primary">
                  <Phone className="size-5" />
                  <span className="text-xs font-bold uppercase tracking-[0.2em] !text-[hsl(0,0%,100%)]">Direct Line</span>
                </div>
                <h3 className="font-serif text-3xl md:text-4xl text-foreground uppercase tracking-tight">713-304-4798</h3>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 mt-8 lg:mt-0">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 shadow-2xl shadow-black/40 text-background !border-none border-1 border-[oklab(0.9999940.00004556780.0000200868/0.2)] md:p-12 lg:p-16">
              <FormProvider formKey="get_quote_form_aboutus" formId="get_quote_form_aboutus" sectionName="contact-us" defaultValues={{
              name: "",
              email: "",
              phone: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              message: {
                required: true
              }
            }} submitText="Get Free Estimate" successMessage="Thank you for reaching out! Steve will get back to you shortly regarding your estimate.">
                <ContactFormContent />
              </FormProvider>
            </div>
          </div>
          
        </div>
      </div>
    </section>;
}